import { URL_API_IMAGES } from '../constants/API'

/**
 * Endpoint externo de correo, vive en el sitio Barrilito (Next.js).
 *
 * OJO: se llama desde el browser, así que la key viaja en el bundle público.
 * No protege nada mientras no exista un proxy server-side. Se manda para que
 * el día que Barrilito valide el header, esto ya funcione.
 *
 * OJO 2: el endpoint tiene Access-Control-Allow-Origin fijo a
 * https://cms.barrilito.com.mx — en localhost el navegador bloquea la llamada.
 */
const MAIL_ENDPOINT = 'https://barrilito.com.mx/api/evento/sendQr'
const MAIL_KEY = process.env.REACT_APP_EVENTO_MAIL_KEY ?? ''

/** Reintentos: solo en 5xx (transitorios). Los 4xx son errores de contrato. */
const MAX_ATTEMPTS = 2

export interface SendQrPayload {
    nombre: string
    email: string
    codigo: string
    qrBase64: string
}

export const qrUrl = (codigo: string) => `${URL_API_IMAGES}QR/${codigo}.png`

/** Lee el PNG ya generado en images/QR/ y lo devuelve como data URL. */
export const getQrBase64 = async (codigo: string): Promise<string> => {

    const response = await fetch(qrUrl(codigo))

    if (!response.ok) {
        throw new Error(`No se encontró el QR del código ${codigo}`)
    }

    const blob = await response.blob()

    return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result as string)
        reader.onerror = () => reject(new Error('No se pudo leer el archivo del QR'))
        reader.readAsDataURL(blob)
    })
}

const sendQr = async (payload: SendQrPayload) => {

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {

        const response = await fetch(MAIL_ENDPOINT, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'x-api-key': MAIL_KEY,
            },
            body: JSON.stringify(payload),
        })

        const data = await response.json().catch(() => ({} as { message?: string }))

        if (response.ok) return data

        const message = data.message || `Error al enviar el correo (${response.status})`

        if (response.status < 500 || attempt === MAX_ATTEMPTS) {
            throw new Error(message)
        }

        await new Promise(resolve => setTimeout(resolve, 500 * attempt))
    }

    throw new Error('Error al enviar el correo')
}

/**
 * Reenvía el pase con QR al participante.
 * Lanza con el message del endpoint para que la UI lo muestre tal cual.
 */
export const reenviarPase = async ({ nombre, email, codigo }: Omit<SendQrPayload, 'qrBase64'>) => {

    const qrBase64 = await getQrBase64(codigo)

    return await sendQr({ nombre, email, codigo, qrBase64 })
}
